import { state } from '../shared/state';
import { $, showToast, bindAutoGrowTextareas, fitAutoGrowTextareas } from '../shared/index';
import { escapeHtml, parseItems, type AvgVideoSeason } from '../avg-video/avg-video-shared';

interface AvgVideoCollection {
  id: string;
  kind: 'series' | 'thematic';
  title: string;
  description?: string;
  /** Публикация подборки — это только статус: отдельного `is_public` больше нет. */
  status: 'draft' | 'published';
  sort_order: number;
  video_ids: string[];
  /** Контракт v1: приходит только у kind='series'. */
  seasons?: AvgVideoSeason[];
}

/**
 * Видео в объёме, нужном редактору состава. Размещение (`series_id`,
 * `season_id`, `episode_position`) приходит в самой карточке — отдельного
 * запроса состава коллекции для админки не требуется.
 */
interface AvgVideoForSelection {
  id: string;
  title: string;
  status: string;
  series_id?: string | null;
  season_id?: string | null;
  episode_position?: number | null;
  collection_ids?: string[];
}

let collectionsCache: AvgVideoCollection[] = [];
let videosCache: AvgVideoForSelection[] = [];
let collectionsBound = false;
let expandedCollectionId: string | null = null;

/** Сезоны раскрытого сериала. Грузятся при раскрытии и после любой правки сезонов. */
let seasonsCache: AvgVideoSeason[] = [];

/**
 * Черновик состава раскрытой коллекции: галочка, сезон и позиция каждой серии.
 * Держится отдельно от DOM — список перерисовывается на каждый клик, и без
 * черновика уже проставленные сезоны терялись бы.
 */
interface ItemDraft {
  checked: boolean;
  seasonId: string;
  position: number;
}

let itemsDraft = new Map<string, ItemDraft>();

/**
 * Что правится инлайн. Раньше названия спрашивал `prompt()`, а удаление —
 * `confirm()`: модальные окна браузера в админке выглядели чужеродно и не
 * показывали, к какой строке относится вопрос.
 */
type CollInline =
  | { mode: 'idle' }
  | { mode: 'rename'; id: string }
  | { mode: 'describe'; id: string }
  | { mode: 'delete'; id: string }
  | { mode: 'season-add'; collectionId: string }
  | { mode: 'season-rename'; seasonId: string }
  | { mode: 'season-delete'; seasonId: string };

let inline: CollInline = { mode: 'idle' };

function kindLabel(kind: 'series' | 'thematic'): string {
  return kind === 'series' ? 'Сериал' : 'Тематическая';
}

function statusLabel(status: 'draft' | 'published'): string {
  return status === 'draft' ? 'Черновик' : 'Опубликовано';
}

function seasonLabel(season: AvgVideoSeason): string {
  return season.title ? `Сезон ${season.number} · ${season.title}` : `Сезон ${season.number}`;
}

function collButton(action: string, id: string, label: string, extraClass = '', seasonId?: string): string {
  const season = seasonId ? ` data-season-id="${escapeHtml(seasonId)}"` : '';
  return `<button type="button" class="atv-mini-btn${extraClass ? ' ' + extraClass : ''}"`
    + ` data-coll-action="${escapeHtml(action)}" data-coll-id="${escapeHtml(id)}"${season}>${escapeHtml(label)}</button>`;
}

/** «↑»/«↓» строки подборки; у крайних кнопка выключена — двигаться некуда. */
function collMoveButtons(coll: AvgVideoCollection, index: number): string {
  const up = index <= 0 ? ' disabled' : '';
  const down = index >= collectionsCache.length - 1 ? ' disabled' : '';
  return `<button type="button" class="atv-mini-btn" data-coll-action="move-up" data-coll-id="${escapeHtml(coll.id)}" title="Выше"${up}>↑</button>`
    + `<button type="button" class="atv-mini-btn" data-coll-action="move-down" data-coll-id="${escapeHtml(coll.id)}" title="Ниже"${down}>↓</button>`;
}

/* ── Сезоны ──────────────────────────────────────────────────────────── */

function renderSeasonsBlock(coll: AvgVideoCollection): string {
  if (coll.kind !== 'series') return '';

  const rows = seasonsCache
    .slice()
    .sort((a, b) => a.number - b.number)
    .map((s) => {
      if (inline.mode === 'season-rename' && inline.seasonId === s.id) {
        return seasonFormHtml('season-rename-apply', coll.id, s);
      }
      if (inline.mode === 'season-delete' && inline.seasonId === s.id) {
        return `
          <div class="atv-inline-confirm">
            <span>Удалить «${escapeHtml(seasonLabel(s))}»? Серии останутся в сериале без сезона.</span>
            ${collButton('season-delete-apply', coll.id, 'Удалить', 'atv-mini-btn--danger', s.id)}
            ${collButton('inline-cancel', coll.id, 'Отмена')}
          </div>
        `;
      }
      return `
        <div class="atv-season-row">
          <span class="atv-season-name">${escapeHtml(seasonLabel(s))}</span>
          <span class="atv-season-count">серий: ${s.item_count}</span>
          ${collButton('season-rename', coll.id, 'Переименовать', '', s.id)}
          ${collButton('season-delete', coll.id, 'Удалить', 'atv-mini-btn--danger', s.id)}
        </div>
      `;
    }).join('');

  const addBlock = inline.mode === 'season-add' && inline.collectionId === coll.id
    ? seasonFormHtml('season-create', coll.id, null)
    : collButton('season-add', coll.id, '＋ Сезон');

  return `
    <div class="atv-season-block">
      <h5 class="atv-season-title">Сезоны</h5>
      ${rows || '<div class="audit-log-empty">Сезонов нет — серии лежат в сериале плоским списком</div>'}
      <div class="atv-inline-form-actions mt-sm">${addBlock}</div>
    </div>
  `;
}

function seasonFormHtml(action: string, collId: string, season: AvgVideoSeason | null): string {
  const nextNumber = season?.number
    ?? (seasonsCache.reduce((max, s) => Math.max(max, s.number), 0) + 1);
  return `
    <div class="atv-inline-form">
      <input type="number" id="admin-atv-season-number" class="setup-input atv-inline-num" min="1" value="${nextNumber}" aria-label="Номер сезона">
      <input type="text" id="admin-atv-season-title" class="setup-input" value="${escapeHtml(season?.title || '')}" placeholder="Название сезона (необязательно)">
      ${collButton(action, collId, 'Сохранить', 'atv-mini-btn--primary', season?.id)}
      ${collButton('inline-cancel', collId, 'Отмена')}
    </div>
  `;
}

async function loadSeasons(collectionId: string): Promise<void> {
  seasonsCache = [];
  try {
    const r = await fetch(`/api/admin/avg-video/collections/${encodeURIComponent(collectionId)}/seasons`, {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (r.ok) {
      const data = await r.json() as { items?: AvgVideoSeason[] };
      seasonsCache = data.items || [];
    }
  } catch {
    seasonsCache = [];
  }
}

/* ── Состав коллекции ────────────────────────────────────────────────── */

function buildItemsDraft(coll: AvgVideoCollection): void {
  itemsDraft = new Map();
  const order = new Map(coll.video_ids.map((id, i) => [id, i + 1]));
  videosCache.forEach((v) => {
    const inCollection = order.has(v.id);
    const isEpisode = coll.kind === 'series' && v.series_id === coll.id;
    itemsDraft.set(v.id, {
      checked: inCollection || isEpisode,
      seasonId: isEpisode ? (v.season_id || '') : '',
      position: (isEpisode ? v.episode_position : null) ?? order.get(v.id) ?? 0,
    });
  });
}

function renderItemsBlock(coll: AvgVideoCollection): string {
  const isSeries = coll.kind === 'series';

  const seasonOptions = seasonsCache
    .slice()
    .sort((a, b) => a.number - b.number)
    .map((s) => `<option value="${escapeHtml(s.id)}">${escapeHtml(seasonLabel(s))}</option>`)
    .join('');

  const rows = videosCache.map((video) => {
    const draft = itemsDraft.get(video.id) || { checked: false, seasonId: '', position: 0 };
    const placement = isSeries && draft.checked
      ? `
        <select class="setup-input atv-coll-season-select" data-video-id="${escapeHtml(video.id)}" aria-label="Сезон серии">
          <option value="">— без сезона —</option>
          ${seasonOptions}
        </select>
        <input type="number" class="setup-input atv-coll-position-input" min="1" value="${draft.position || ''}"
               data-video-id="${escapeHtml(video.id)}" placeholder="№" aria-label="Номер серии">
      `
      : '';

    return `
      <div class="atv-coll-item-row">
        <label class="atv-coll-video-check">
          <input type="checkbox" class="atv-coll-video-checkbox" value="${escapeHtml(video.id)}"
                 data-collection-id="${escapeHtml(coll.id)}"${draft.checked ? ' checked' : ''}>
        </label>
        <span class="atv-coll-item-title">${escapeHtml(video.title)}</span>
        ${placement}
      </div>
    `;
  }).join('');

  return `
    <div class="atv-coll-items-block">
      ${renderSeasonsBlock(coll)}
      <div class="atv-coll-items-list">
        ${rows || '<div class="audit-log-empty">Видео не найдены</div>'}
      </div>
      <div class="atv-coll-items-buttons">
        ${collButton('save-items', coll.id, 'Сохранить состав', 'atv-mini-btn--primary')}
        ${collButton('collapse', coll.id, 'Свернуть')}
      </div>
    </div>
  `;
}

function renderCollectionsList(): void {
  const container = $('admin-atv-coll-list');
  if (!container) return;

  if (collectionsCache.length === 0) {
    container.innerHTML = '<div class="audit-log-empty">Подборки не созданы</div>';
    return;
  }

  container.innerHTML = collectionsCache.map((coll, index) => {
    if (inline.mode === 'rename' && inline.id === coll.id) {
      return `
        <div class="atv-coll-row">
          <div class="atv-inline-form">
            <input type="text" id="admin-atv-coll-input" class="setup-input" value="${escapeHtml(coll.title)}" placeholder="Название подборки">
            ${collButton('rename-apply', coll.id, 'Сохранить', 'atv-mini-btn--primary')}
            ${collButton('inline-cancel', coll.id, 'Отмена')}
          </div>
        </div>
      `;
    }

    /* Описание видно пользователю на карточке-входе, поэтому правится здесь же,
       рядом с названием, а не только при создании. */
    if (inline.mode === 'describe' && inline.id === coll.id) {
      return `
        <div class="atv-coll-row">
          <div class="atv-inline-form atv-inline-form--column">
            <textarea id="admin-atv-coll-desc" class="setup-input atv-md-input" rows="10" data-atv-autogrow placeholder="Markdown: **важное**, списки через «- », ссылки [текст](адрес). Пустая строка разделяет абзацы.">${escapeHtml(coll.description || '')}</textarea>
            <p class="setup-hint">Текст сохраняется как есть, с разметкой. Пользователь видит его на экране подборки; на карточке-входе разметка снимается.</p>
            <div class="atv-inline-form-actions">
              ${collButton('describe-apply', coll.id, 'Сохранить', 'atv-mini-btn--primary')}
              ${collButton('inline-cancel', coll.id, 'Отмена')}
            </div>
          </div>
        </div>
      `;
    }

    if (inline.mode === 'delete' && inline.id === coll.id) {
      return `
        <div class="atv-coll-row">
          <div class="atv-inline-confirm">
            <span>Удалить подборку «${escapeHtml(coll.title)}»? Видео останутся в каталоге.</span>
            ${collButton('delete-apply', coll.id, 'Удалить', 'atv-mini-btn--danger')}
            ${collButton('inline-cancel', coll.id, 'Отмена')}
          </div>
        </div>
      `;
    }

    const statusBadgeClass = coll.status === 'published' ? 'atv-status-published' : 'atv-status-draft';
    const publishBtn = coll.status === 'draft'
      ? collButton('publish', coll.id, 'Опубликовать', 'atv-mini-btn--success')
      : collButton('unpublish', coll.id, 'В черновик', 'atv-mini-btn--muted');

    const isExpanded = expandedCollectionId === coll.id;

    return `
      <div class="atv-coll-row">
        <div class="atv-coll-header">
          <div class="atv-coll-info">
            <div class="atv-coll-title">${escapeHtml(coll.title)}</div>
            <div class="atv-coll-badges">
              <span class="atv-badge">${escapeHtml(kindLabel(coll.kind))}</span>
              <span class="atv-badge atv-badge-muted">${coll.description?.trim() ? 'описание есть' : 'без описания'}</span>
              <span class="atv-badge atv-status-badge ${statusBadgeClass}">${escapeHtml(statusLabel(coll.status))}</span>
              <span class="atv-badge atv-badge-muted">видео: ${coll.video_ids.length}</span>
            </div>
          </div>
          <div class="atv-coll-actions">
            ${collMoveButtons(coll, index)}
            ${collButton('rename', coll.id, 'Переименовать')}
            ${collButton('describe', coll.id, 'Описание')}
            ${collButton('items', coll.id, isExpanded ? 'Скрыть состав' : 'Состав')}
            ${collButton('delete', coll.id, 'Удалить', 'atv-mini-btn--danger')}
            ${publishBtn}
          </div>
        </div>
        ${isExpanded ? renderItemsBlock(coll) : ''}
      </div>
    `;
  }).join('');

  // Значения `select` в разметке не проставляются (у сезонов нет `selected`) —
  // выставляем их из черновика после вставки HTML.
  document.querySelectorAll<HTMLSelectElement>('.atv-coll-season-select').forEach((select) => {
    const draft = itemsDraft.get(select.dataset.videoId || '');
    select.value = draft?.seasonId || '';
  });

  // Поля пересоздаются при каждой перерисовке — высоту подгоняем заново.
  fitAutoGrowTextareas();

  $<HTMLInputElement>('admin-atv-coll-input')?.focus();
  $<HTMLInputElement>('admin-atv-season-title')?.focus();
  $<HTMLTextAreaElement>('admin-atv-coll-desc')?.focus();
}

async function loadCollections(): Promise<void> {
  const container = $('admin-atv-coll-list');
  if (container) container.innerHTML = '<div class="audit-log-empty">Загрузка…</div>';

  try {
    const [collRes, vidRes] = await Promise.all([
      fetch('/api/admin/avg-video/collections', {
        headers: { Authorization: 'Bearer ' + state.authToken },
      }),
      fetch('/api/admin/avg-video/videos', {
        headers: { Authorization: 'Bearer ' + state.authToken },
      }),
    ]);

    if (!collRes.ok) throw new Error('Не удалось загрузить подборки');
    collectionsCache = parseItems<AvgVideoCollection>(await collRes.json());

    if (vidRes.ok) {
      videosCache = parseItems<AvgVideoForSelection>(await vidRes.json());
    }

    // Раскрытая коллекция переживает перезагрузку: правка сезона не должна
    // схлопывать редактор состава, над которым админ сейчас работает.
    const keepExpanded = expandedCollectionId;
    const coll = collectionsCache.find((c) => c.id === keepExpanded);
    expandedCollectionId = coll ? keepExpanded : null;
    if (coll) {
      if (coll.kind === 'series') await loadSeasons(coll.id);
      buildItemsDraft(coll);
    } else {
      seasonsCache = [];
      itemsDraft = new Map();
    }
    renderCollectionsList();
  } catch (err) {
    if (container) container.innerHTML = '<div class="audit-log-empty">Ошибка загрузки</div>';
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  }
}

async function createCollection(): Promise<void> {
  const titleEl = $<HTMLInputElement>('admin-atv-coll-title');
  const kindEl = $<HTMLSelectElement>('admin-atv-coll-kind');

  const title = titleEl?.value.trim() || '';
  const kind = (kindEl?.value as 'series' | 'thematic') || 'series';

  if (!title) {
    showToast('❌ Укажите название подборки');
    return;
  }

  const r = await fetch('/api/admin/avg-video/collections', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify({ title, kind }),
  });

  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { detail?: string; message?: string };
    showToast('❌ ' + (err.detail || err.message || 'Ошибка создания'));
    return;
  }

  showToast('✅ Подборка создана');
  if (titleEl) titleEl.value = '';
  await loadCollections();
}

async function renameCollection(id: string): Promise<void> {
  const coll = collectionsCache.find((c) => c.id === id);
  const newTitle = $<HTMLInputElement>('admin-atv-coll-input')?.value.trim() || '';
  if (!coll) return;
  if (!newTitle) {
    showToast('❌ Название не может быть пустым');
    return;
  }
  if (newTitle === coll.title) {
    inline = { mode: 'idle' };
    renderCollectionsList();
    return;
  }

  const r = await fetch(`/api/admin/avg-video/collections/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify({ title: newTitle }),
  });

  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { detail?: string; message?: string };
    showToast('❌ ' + (err.detail || err.message || 'Ошибка сохранения'));
    return;
  }

  showToast('✅ Название обновлено');
  inline = { mode: 'idle' };
  await loadCollections();
}

/**
 * Описание подборки — то, что читает пользователь на карточке-входе.
 *
 * Пустой текст сохраняем осознанно: это способ убрать описание. Пустая строка
 * доезжает до сервера как `null`, а не пропускается, — иначе стереть текст было
 * бы нечем.
 */
async function saveCollectionDescription(id: string): Promise<void> {
  const coll = collectionsCache.find((c) => c.id === id);
  if (!coll) return;
  const value = $<HTMLTextAreaElement>('admin-atv-coll-desc')?.value.trim() || '';

  if (value === (coll.description || '').trim()) {
    inline = { mode: 'idle' };
    renderCollectionsList();
    return;
  }

  const r = await fetch(`/api/admin/avg-video/collections/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify({ description: value }),
  });

  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { detail?: string; message?: string };
    showToast('❌ ' + (err.detail || err.message || 'Ошибка сохранения'));
    return;
  }

  showToast(value ? '✅ Описание обновлено' : '✅ Описание очищено');
  inline = { mode: 'idle' };
  await loadCollections();
}

async function deleteCollection(id: string): Promise<void> {
  const r = await fetch(`/api/admin/avg-video/collections/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: 'Bearer ' + state.authToken },
  });

  if (!r.ok) {
    showToast('❌ Не удалось удалить подборку');
    return;
  }

  showToast('✅ Подборка удалена');
  inline = { mode: 'idle' };
  if (expandedCollectionId === id) expandedCollectionId = null;
  await loadCollections();
}

async function setCollectionStatus(id: string, status: 'draft' | 'published'): Promise<void> {
  const body = { status };
  const r = await fetch(`/api/admin/avg-video/collections/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify(body),
  });

  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { detail?: string; message?: string };
    showToast('❌ ' + (err.detail || err.message || 'Ошибка'));
    return;
  }

  showToast(status === 'published' ? '✅ Подборка опубликована' : '✅ Подборка переведена в черновик');
  await loadCollections();
}

async function putCollectionSortOrder(id: string, sortOrder: number): Promise<boolean> {
  const r = await fetch(`/api/admin/avg-video/collections/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify({ sort_order: sortOrder }),
  });
  return r.ok;
}

/**
 * Переставить подборку: обмен значениями `sort_order` с соседом по списку
 * (сервер отдаёт подборки уже в порядке `sort_order`).
 *
 * Если у соседей порядок совпадает — а у созданных подборок это нули, — обмен
 * ничего бы не изменил, поэтому список перенумеровывается по позициям.
 */
async function moveCollection(id: string, delta: -1 | 1): Promise<void> {
  const index = collectionsCache.findIndex((c) => c.id === id);
  const current = collectionsCache[index];
  const neighbour = collectionsCache[index + delta];
  if (!current || !neighbour) return;

  const ordered = collectionsCache.slice();
  ordered[index] = neighbour;
  ordered[index + delta] = current;

  const updates = current.sort_order === neighbour.sort_order
    ? ordered
      .map((c, i) => ({ id: c.id, sort_order: i }))
      .filter((u, i) => ordered[i].sort_order !== u.sort_order)
    : [
      { id: current.id, sort_order: neighbour.sort_order },
      { id: neighbour.id, sort_order: current.sort_order },
    ];

  const results = await Promise.all(updates.map((u) => putCollectionSortOrder(u.id, u.sort_order)));
  if (results.some((ok) => !ok)) {
    showToast('❌ Не удалось изменить порядок подборок');
  }
  await loadCollections();
}

/* ── Сезоны: запись ──────────────────────────────────────────────────── */

function readSeasonForm(): { number: number; title: string | null } | null {
  const numberRaw = $<HTMLInputElement>('admin-atv-season-number')?.value || '';
  const number = parseInt(numberRaw, 10);
  if (!Number.isFinite(number) || number < 1) {
    showToast('❌ Номер сезона — целое число от 1');
    return null;
  }
  return { number, title: $<HTMLInputElement>('admin-atv-season-title')?.value.trim() || null };
}

async function createSeason(collectionId: string): Promise<void> {
  const body = readSeasonForm();
  if (!body) return;

  const r = await fetch(`/api/admin/avg-video/collections/${encodeURIComponent(collectionId)}/seasons`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify(body),
  });

  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { detail?: string; message?: string };
    showToast('❌ ' + (err.detail || err.message || 'Не удалось создать сезон'));
    return;
  }

  showToast('✅ Сезон создан');
  inline = { mode: 'idle' };
  await loadSeasons(collectionId);
  renderCollectionsList();
}

async function renameSeason(seasonId: string, collectionId: string): Promise<void> {
  const body = readSeasonForm();
  if (!body) return;

  const r = await fetch(`/api/admin/avg-video/seasons/${encodeURIComponent(seasonId)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify(body),
  });

  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { detail?: string; message?: string };
    showToast('❌ ' + (err.detail || err.message || 'Не удалось сохранить сезон'));
    return;
  }

  showToast('✅ Сезон обновлён');
  inline = { mode: 'idle' };
  await loadSeasons(collectionId);
  renderCollectionsList();
}

async function deleteSeason(seasonId: string, collectionId: string): Promise<void> {
  const r = await fetch(`/api/admin/avg-video/seasons/${encodeURIComponent(seasonId)}`, {
    method: 'DELETE',
    headers: { Authorization: 'Bearer ' + state.authToken },
  });

  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { detail?: string; message?: string };
    showToast('❌ ' + (err.detail || err.message || 'Не удалось удалить сезон'));
    return;
  }

  showToast('✅ Сезон удалён — серии остались в сериале');
  inline = { mode: 'idle' };
  // Серии удалённого сезона переходят в «без сезона» — черновик состава обновляем.
  itemsDraft.forEach((draft) => {
    if (draft.seasonId === seasonId) draft.seasonId = '';
  });
  await loadSeasons(collectionId);
  renderCollectionsList();
}

/* ── Состав: запись ──────────────────────────────────────────────────── */

async function saveItemsForCollection(id: string): Promise<void> {
  const coll = collectionsCache.find((c) => c.id === id);
  if (!coll) return;

  // Новая форма контракта: у каждой серии свой сезон и позиция. Позицию не
  // указали — считаем по порядку в списке.
  let fallbackPosition = 0;
  const items = videosCache
    .filter((v) => itemsDraft.get(v.id)?.checked)
    .map((v) => {
      const draft = itemsDraft.get(v.id)!;
      fallbackPosition += 1;
      return {
        video_id: v.id,
        season_id: coll.kind === 'series' ? (draft.seasonId || null) : null,
        position: draft.position || fallbackPosition,
      };
    });

  const r = await fetch(`/api/admin/avg-video/collections/${encodeURIComponent(id)}/items`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify({ items }),
  });

  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { detail?: string; message?: string };
    showToast('❌ ' + (err.detail || err.message || 'Ошибка сохранения состава'));
    return;
  }

  showToast('✅ Состав сохранён');
  await loadCollections();
}

async function toggleExpanded(id: string): Promise<void> {
  if (expandedCollectionId === id) {
    expandedCollectionId = null;
    seasonsCache = [];
    itemsDraft = new Map();
    renderCollectionsList();
    return;
  }

  const coll = collectionsCache.find((c) => c.id === id);
  if (!coll) return;

  expandedCollectionId = id;
  inline = { mode: 'idle' };
  seasonsCache = [];
  if (coll.kind === 'series') await loadSeasons(id);
  buildItemsDraft(coll);
  renderCollectionsList();
}

export async function loadAdminAvgVideoCollections(): Promise<void> {
  bindAdminAvgVideoCollectionsEvents();
  await loadCollections();
}

export function bindAdminAvgVideoCollectionsEvents(): void {
  if (collectionsBound) return;
  collectionsBound = true;

  bindAutoGrowTextareas();
  $('btn-admin-atv-coll-create')?.addEventListener('click', () => void createCollection());

  $('admin-atv-coll-list')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement;
    const btn = target.closest<HTMLElement>('[data-coll-action]');
    if (!btn?.dataset.collAction || !btn.dataset.collId) return;

    const action = btn.dataset.collAction;
    const collId = btn.dataset.collId;
    const seasonId = btn.dataset.seasonId || '';

    if (action === 'rename') {
      inline = { mode: 'rename', id: collId };
      renderCollectionsList();
    } else if (action === 'rename-apply') {
      void renameCollection(collId);
    } else if (action === 'describe') {
      inline = { mode: 'describe', id: collId };
      renderCollectionsList();
    } else if (action === 'describe-apply') {
      void saveCollectionDescription(collId);
    } else if (action === 'delete') {
      inline = { mode: 'delete', id: collId };
      renderCollectionsList();
    } else if (action === 'delete-apply') {
      void deleteCollection(collId);
    } else if (action === 'inline-cancel') {
      inline = { mode: 'idle' };
      renderCollectionsList();
    } else if (action === 'publish') {
      void setCollectionStatus(collId, 'published');
    } else if (action === 'unpublish') {
      void setCollectionStatus(collId, 'draft');
    } else if (action === 'move-up') {
      void moveCollection(collId, -1);
    } else if (action === 'move-down') {
      void moveCollection(collId, 1);
    } else if (action === 'items') {
      void toggleExpanded(collId);
    } else if (action === 'save-items') {
      void saveItemsForCollection(collId);
    } else if (action === 'collapse') {
      expandedCollectionId = null;
      seasonsCache = [];
      itemsDraft = new Map();
      renderCollectionsList();
    } else if (action === 'season-add') {
      inline = { mode: 'season-add', collectionId: collId };
      renderCollectionsList();
    } else if (action === 'season-create') {
      void createSeason(collId);
    } else if (action === 'season-rename') {
      inline = { mode: 'season-rename', seasonId };
      renderCollectionsList();
    } else if (action === 'season-rename-apply') {
      void renameSeason(seasonId, collId);
    } else if (action === 'season-delete') {
      inline = { mode: 'season-delete', seasonId };
      renderCollectionsList();
    } else if (action === 'season-delete-apply') {
      void deleteSeason(seasonId, collId);
    }
  });

  $('admin-atv-coll-list')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLElement;

    const checkbox = target.closest<HTMLInputElement>('.atv-coll-video-checkbox');
    if (checkbox) {
      const draft = itemsDraft.get(checkbox.value);
      if (draft) draft.checked = checkbox.checked;
      // Перерисовка нужна: у отмеченной серии появляются сезон и номер.
      renderCollectionsList();
      return;
    }

    const seasonSelect = target.closest<HTMLSelectElement>('.atv-coll-season-select');
    if (seasonSelect?.dataset.videoId) {
      const draft = itemsDraft.get(seasonSelect.dataset.videoId);
      if (draft) draft.seasonId = seasonSelect.value;
      return;
    }

    const positionInput = target.closest<HTMLInputElement>('.atv-coll-position-input');
    if (positionInput?.dataset.videoId) {
      const draft = itemsDraft.get(positionInput.dataset.videoId);
      if (draft) draft.position = parseInt(positionInput.value, 10) || 0;
    }
  });
}
